import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'arbauto_access_token';
const USER_STORAGE_KEY = 'arbauto_user';

export function useAuth() {
  // null = checking stored token, false = not authenticated, true = authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const validateToken = useCallback(async (token) => {
    if (!token) {
      setIsAuthenticated(false);
      return false;
    }

    setIsValidating(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.valid) {
        const userProfile = {
          email: data.email,
          name: data.name || null,
          surname: data.surname || null,
          company: data.company || null,
        };
        setIsAuthenticated(true);
        setUser(userProfile);
        localStorage.setItem(STORAGE_KEY, token);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userProfile));
        return true;
      } else {
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
        setError(data.error || 'Invalid access code');
        return false;
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setIsAuthenticated(false);
      setError('Connection error. Please try again.');
      return false;
    } finally {
      setIsValidating(false);
    }
  }, []);

  const login = useCallback(async (token) => {
    return await validateToken(token);
  }, [validateToken]);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
  }, []);

  // On mount: check for stored token
  useEffect(() => {
    const storedToken = localStorage.getItem(STORAGE_KEY);
    if (storedToken) {
      validateToken(storedToken);
    } else {
      setIsAuthenticated(false);
    }
  }, [validateToken]);

  return { isAuthenticated, isValidating, error, user, login, logout };
}
